import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { LayoutDashboard, Building2, Bell, Settings, LogOut, Activity, Users as UsersIcon, Map as MapIcon, BarChart3, FileText, History, Hammer, Inbox, ShieldCheck, UserCog, Scale } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

const ROLE_BADGE = {
  super_admin: "bg-slate-900 text-white",
  admin: "text-white",
  manager: "bg-emerald-700 text-white",
  editor: "bg-sky-700 text-white",
  viewer: "bg-slate-200 text-slate-700",
};

const ROLE_STYLE = {
  admin: { backgroundColor: "var(--hi-olive)" },
};

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/properties", label: "Properties", icon: Building2 },
  { to: "/map", label: "Map", icon: MapIcon },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/programme", label: "Programme", icon: FileText },
  { to: "/delivery", label: "Delivery", icon: Hammer },
  { to: "/inbox", label: "Job-Card Inbox", icon: Inbox },
  { to: "/alerts", label: "Alerts", icon: Bell },
  { to: "/audit", label: "Audit Log", icon: History, managerOnly: true },
  { to: "/users", label: "Users", icon: UsersIcon, adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, superOnly: true },
  { to: "/governance", label: "Governance", icon: Scale, superOnly: true },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "viewer";
  const location = useLocation();
  const [dpaOpen, setDpaOpen] = useState(0);
  useEffect(() => {
    if (role !== "super_admin") return undefined;
    const refresh = () => api.get("/dpa/reminders/count").then(({ data }) => setDpaOpen(data.open)).catch(() => {});
    refresh();
    window.addEventListener("dpa-reminders-changed", refresh);
    return () => window.removeEventListener("dpa-reminders-changed", refresh);
  }, [role, location.pathname]);

  const filteredNav = NAV.filter((item) => {
    if (item.superOnly && role !== "super_admin") return false;
    if (item.adminOnly && !["super_admin", "admin"].includes(role)) return false;
    if (item.managerOnly && !["super_admin", "admin", "manager"].includes(role)) return false;
    return true;
  });

  return (
    <div className="min-h-screen text-foreground flex" style={{ backgroundColor: "var(--hi-cream)" }}>
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-white flex flex-col" style={{ borderRight: "1px solid var(--hi-border)" }} data-testid="sidebar">
        <div className="px-5 py-5 border-b" style={{ borderColor: "var(--hi-border)" }}>
          <div className="flex items-center gap-3">
            <img
              src="/brand/housing-intelligence-logo.png"
              alt="Housing Intelligence"
              className="h-10 w-10 object-contain"
              data-testid="brand-logo"
            />
            <div className="leading-tight">
              <div className="font-heading font-black text-[15px]" style={{ color: "var(--hi-olive)" }}>Housing</div>
              <div className="font-heading font-black text-[15px] -mt-0.5" style={{ color: "var(--hi-olive)" }}>Intelligence</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground mt-1">EPC Register</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {filteredNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "text-white"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                )
              }
              style={({ isActive }) => isActive ? { backgroundColor: "var(--hi-olive)" } : undefined}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {item.to === "/compliance" && dpaOpen > 0 && (
                <span className="ml-auto min-w-[20px] text-center px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white" title="Open DPA reminders" data-testid="nav-dpa-reminder-badge">{dpaOpen}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Signed in</div>
            <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
            <span className={`inline-block mt-1.5 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE[role] || ROLE_BADGE.viewer}`} style={ROLE_STYLE[role]} data-testid="current-user-role">
              {role.replace("_", " ")}
            </span>
          </div>
          <NavLink to="/account" data-testid="nav-my-account"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors">
            <UserCog className="h-4 w-4" />
            My account
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 bg-white px-8 flex items-center justify-between" style={{ borderBottom: "1px solid var(--hi-border)" }}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--hi-olive)" }}>
            <Activity className="h-3.5 w-3.5" />
            <span>Housing Intelligence · EPC Register Monitoring</span>
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            {new Date().toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
          </div>
        </header>
        <div className="flex-1 overflow-auto">{children}</div>
      </main>
    </div>
  );
}
